"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { m } from "framer-motion";
import Container from "~/componentsDS/Layout/Container";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Lead from "~/componentsDS/Typography/Lead";
import Badge from "~/componentsDS/Badge";
import { useClientMediaQuery } from "~/hooks/useClientMediaQuery";
import { heroMedia } from "./data";
import { fadeUp } from "./config/animationConfig";
import { cn } from "~/lib/utils";

const FounderQuote = ({ className }: { className?: string }) => (
    <div className={cn(
        "px-6 md:px-12 pt-6 pb-10 md:pb-6 max-lg:mt-auto text-left bg-trueBlack/10 backdrop-blur-[25px] rounded-t-2xl md:rounded-b-2xl overflow-hidden",
        className
    )}>
        <Lead font="featureDisplay" size="sm" sizes={{ lg: "md" }} className="relative tracking-[0.01em] lg:pr-4 mb-5">
            <span className="inline-block absolute top-0 left-0 -translate-x-full">&ldquo;</span>
            I started Inner Balance because the way we approach women&apos;s health isn&apos;t working.&rdquo;
        </Lead>
        <Paragraph size="md" sizes={{ lg: "xl" }} className="mb-2 lg:mb-3">
            Too often, women are told everything is normal when they clearly don&apos;t feel normal. Symptoms are dismissed, or treated in isolation, without looking at the full picture. <br />
            <br />
            As a physician, I saw this pattern constantly. The issue isn&apos;t a lack of science — it&apos;s how that science is applied. <br />
            <br />
            Inner Balance was built to take a more complete approach. One that looks at the full system, treats earlier, and focuses on how women actually feel.
        </Paragraph>
        <div className="relative aspect-[210/89] w-[140px] lg:w-[210px]">
            <Image
                src={heroMedia.founderSignature}
                alt="Signature"
                fill
                sizes="210px"
                className="object-contain"
            />
        </div>
    </div>
);

const NoteFromFounder = () => {
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const isMobile = useClientMediaQuery("(max-width: 639px)");
    const videoSrc = shouldLoadVideo && isMobile !== null
        ? (isMobile ? heroMedia.founderVideoMobile : heroMedia.founderVideo)
        : undefined;

    useEffect(() => {
        if (window.scrollY > 0) {
            setShouldLoadVideo(true);
            return;
        }

        const handleScroll = () => setShouldLoadVideo(true);
        window.addEventListener("scroll", handleScroll, { once: true, passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <section className="relative lg:min-h-screen overflow-hidden -mt-4 md:-mt-6 bg-[linear-gradient(179deg,rgba(4,9,26,0)_27.39%,rgba(4,9,26,0.5)_37.67%,rgba(27,28,31,0.92)_102.34%)] rounded-2xl md:rounded-3xl z-[10]">
            <div className="
                absolute inset-0 z-[1]
                bg-[linear-gradient(0deg,rgba(84,114,204,0.05)_0%,rgba(84,114,204,0.05)_100%),linear-gradient(179deg,rgba(4,9,26,0)_27.39%,rgba(4,9,26,0.5)_37.67%,rgba(27,28,31,0.92)_102.34%)]
                lg:bg-[linear-gradient(0deg,rgba(84,114,204,0.05)_0%,rgba(84,114,204,0.05)_100%),linear-gradient(270deg,rgba(0,0,0,0.51)_0%,rgba(102,102,102,0)_63.51%)]
                opacity-20 lg:opacity-50
            " />
            <picture className="absolute inset-0">
                <source media="(max-width: 639px)" srcSet={heroMedia.founderVideoMobilePoster} />
                <img
                    src={heroMedia.founderVideoPoster}
                    alt=""
                    className="w-full h-auto sm:size-full sm:object-cover"
                />
            </picture>

            <video
                className="absolute inset-0 w-full sm:size-full object-cover"
                src={videoSrc}
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                aria-hidden="true"
                tabIndex={-1}
                disablePictureInPicture
                disableRemotePlayback
            />

            <div className="absolute inset-0 bg-[rgba(84,114,204,0.05)] bg-blend-normal" />
            <div className="absolute inset-0 bg-[linear-gradient(270deg,rgba(0,0,0,0.51)_0%,rgba(102,102,102,0)_63.51%)]" />

            <Container className="relative z-10 flex lg:min-h-screen lg:items-center max-md:px-0 pt-8 md:py-24">
                <div className="flex w-full justify-center lg:justify-end">
                    <m.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
                        className="max-lg:hidden w-full flex justify-center items-end"
                    >
                        <div className="w-[385px] flex flex-col items-center p-4 border border-pureWhite/10 bg-trueBlack/25 backdrop-blur-[10px] rounded-lg overflow-hidden">
                            <Lead size="xs" className="text-pureWhite mb-2">Dr. Sarah Daccarett, MD</Lead>
                            <Badge variant="secondary">
                                <Paragraph className="pt-[2px]">Founder & CEO, Inner Balance</Paragraph>
                            </Badge>
                        </div>
                    </m.div>

                    <div className="flex flex-col lg:max-w-[630px] w-full shrink-0 text-center text-pureWhite">
                        <m.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                        >
                            <Heading
                                as="h2"
                                size="md"
                                sizes={{ md: "lg" }}
                                className="mb-2 max-md:mt-4"
                            >
                                A note from our{" "}
                                <Heading
                                    as="i"
                                    weight="400"
                                    font="featureDisplay"
                                    className="text-[36px] md:text-[43px]"
                                >
                                    founder
                                </Heading>
                            </Heading>

                            <Heading as="h3" size="2xs" sizes={{ md: "xs" }} weight="400" className="mb-[340px] lg:mb-6">
                                Dr. Sarah Daccarett, CEO
                            </Heading>
                        </m.div>

                        <FounderQuote className="sm:hidden" />

                        <m.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
                            className="max-sm:hidden"
                        >
                            <FounderQuote />
                        </m.div>
                    </div>
                </div>
            </Container>
        </section>
    );
};

export default NoteFromFounder;
